<template>
	<aside
		class="min-w-0"
		:class="
			messageOnly
				? ''
				: 'self-start overflow-hidden rounded-lg border border-grey-200 lg:sticky lg:top-4'
		"
		:aria-busy="String(loading)"
	>
		<h2
			v-if="!messageOnly"
			class="m-0 bg-grey-50 px-4 py-2.5 font-sans text-sm font-normal uppercase text-grey-900"
		>
			{{ $t('email-templates.preview') }}
		</h2>
		<dl
			class="m-0 space-y-1 border-b border-grey-200 p-4 text-grey-900"
			:class="messageOnly ? 'text-xs' : 'text-sm'"
		>
			<div
				v-for="field in metadataFields"
				:key="field"
				class="flex items-baseline"
			>
				<dt class="w-[60px] shrink-0 text-xs font-normal text-grey-600">
					{{ $t('email-templates.' + field) }}
				</dt>
				<dd class="m-0 min-w-0 flex-1 break-words">
					<slot v-if="field === 'bcc'" name="bcc">{{
						bcc.join(', ') || '–'
					}}</slot>
					<template v-else>{{
						preview && preview[field] ? preview[field] : '–'
					}}</template>
				</dd>
			</div>
		</dl>
		<p
			v-if="error && errorMessage"
			role="status"
			class="m-0 p-4 text-sm text-grey-600"
		>
			{{ errorMessage }}
		</p>
		<p
			v-else-if="!preview && !error"
			role="status"
			class="m-0 p-4 text-sm text-grey-600"
		>
			{{ $t('general.loading') }}
		</p>
		<iframe
			v-else-if="preview"
			ref="frame"
			:srcdoc="documentHtml"
			sandbox="allow-same-origin"
			:title="$t('email-templates.preview')"
			:height="height"
			class="block w-full border-0 bg-white"
			@load="resize"
		/>
	</aside>
</template>
<script>
export default {
	props: {
		preview: Object,
		messageOnly: Boolean,
		loading: Boolean,
		error: Boolean,
		errorMessage: String,
		bcc: { type: Array, default: () => [] }
	},
	data: () => ({ height: 480, observer: null, resizeFrame: null }),
	computed: {
		metadataFields() {
			return this.messageOnly
				? ['from', 'to', 'bcc', 'subject']
				: ['from', 'to', 'subject', ...(this.bcc.length ? ['bcc'] : [])]
		},
		documentHtml() {
			if (!this.preview || !this.messageOnly)
				return this.preview?.html || ''
			const heading = (this.preview.heading || '').replace(
				/[&<>"']/g,
				character =>
					({
						'&': '&amp;',
						'<': '&lt;',
						'>': '&gt;',
						'"': '&quot;',
						"'": '&#39;'
					}[character])
			)
			// Body HTML is sanitized by TemplateRenderer; the sandbox also disables scripts.
			return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');body{box-sizing:border-box;margin:0;padding:16px;font-family:Roboto,Arial,sans-serif;font-size:14px;line-height:1.5;color:#171717;overflow-wrap:anywhere}h1{font-size:14px;font-weight:500;margin:0 0 8px}p{margin:0 0 8px}p:last-child{margin-bottom:0}a{color:inherit;text-decoration:underline}img{max-width:100%;height:auto}</style></head><body>${
				heading ? `<h1>${heading}</h1>` : ''
			}${this.preview.body || ''}</body></html>`
		}
	},
	methods: {
		clearPreviewObserver() {
			if (this.observer) {
				this.observer.disconnect()
				this.observer = null
			}
			if (this.resizeFrame !== null) {
				window.cancelAnimationFrame(this.resizeFrame)
				this.resizeFrame = null
			}
		},
		resize() {
			this.clearPreviewObserver()
			// Same-origin access measures the sanitized preview; scripts remain disabled.
			const body =
				this.$refs.frame &&
				this.$refs.frame.contentDocument &&
				this.$refs.frame.contentDocument.body
			if (!body) return
			const measure = () => {
				if (this._isDestroyed) return
				const next = Math.ceil(body.getBoundingClientRect().height)
				if (next > 0 && next !== this.height) this.height = next
			}
			measure()
			if (typeof ResizeObserver === 'undefined') return
			// Chrome reports a window error when this callback changes layout
			// before the browser finishes delivering resize notifications.
			let pending = false
			this.observer = new ResizeObserver(() => {
				if (pending) return
				pending = true
				this.resizeFrame = window.requestAnimationFrame(() => {
					this.resizeFrame = null
					pending = false
					measure()
				})
			})
			this.observer.observe(body)
		}
	},
	beforeDestroy() {
		this.clearPreviewObserver()
	}
}
</script>
